import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../config/firebase';

export interface WeddingDetails {
  date: Date;
  location: {
    city: string;
    venue?: string;
    address?: string;
  };
  budget: number;
  guestCount: number;
}

export interface UserProfile {
  id: string;
  email: string;
  displayName?: string;
  partner1: {
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
  };
  partner2: {
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
  };
  weddingDetails: WeddingDetails;
  createdAt: Date;
  updatedAt: Date;
  onboardingCompleted: boolean;
  subscription: 'free' | 'essential' | 'pro';
}

export const userService = {
  async createUserProfile(userId: string, data: any) {
    try {
      // Vérifier si l'utilisateur est anonyme (invité public)
      const auth = getAuth();
      if (auth.currentUser?.isAnonymous) {
        // // // // // // // // // // // // // // // // console.log('Utilisateur anonyme détecté, création de profil ignorée');
        return null;
      }

      // Vérifier si le profil existe déjà pour éviter d'écraser des données
      const existingProfile = await this.getUserProfile(userId);
      if (existingProfile) {
        // // // // // // // // // // // // // // // // console.log(`Profil existant trouvé pour ${userId}, fusion des données...`);
        // Mettre à jour le profil existant avec les nouvelles données tout en préservant les données existantes
        return this.updateUserProfile(userId, {
          ...data,
          updatedAt: new Date()
        });
      }

      // S'assurer que les champs requis sont présents
      const defaultProfile = {
        id: userId,
        email: '',
        displayName: '',
        partner1: {
          firstName: '',
          lastName: '',
        },
        partner2: {
          firstName: '',
          lastName: '',
        },
        weddingDetails: {
          date: new Date(),
          location: {
            city: '',
          },
          budget: 0,
          guestCount: 0,
        },
        createdAt: new Date(),
        updatedAt: new Date(),
        onboardingCompleted: false,
        subscription: 'free'
      };

      // Fusionner avec les données fournies
      const profileData = {
        ...defaultProfile,
        ...data,
        // S'assurer que ces champs sont toujours présents
        createdAt: data.createdAt || new Date(),
        updatedAt: new Date(),
        id: userId, // Toujours utiliser l'ID fourni
      };

      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, profileData);
      // // // // // // // // // // // // // // // // console.log(`Profil utilisateur créé avec succès pour ${userId}`);
      return profileData;
    } catch (error) {
      console.error(`Erreur lors de la création du profil utilisateur pour ${userId}:`, error);
      throw error;
    }
  },

  async getUserProfile(userId: string) {
    try {
      // Les utilisateurs anonymes n'ont pas de document profil
      if (getAuth().currentUser?.isAnonymous) {
        return null;
      }

      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        // // // // // // // // // // // // // // // // console.log('Profil utilisateur non trouvé pour:', userId);
        return null;
      }

      return userDoc.data();
    } catch (error) {
      console.error('Erreur lors de la récupération du profil utilisateur:', error);
      return null;
    }
  },

  async updateUserProfile(userId: string, data: any) {
    try {
      const userRef = doc(db, 'users', userId);

      // Vérifier que le document existe avant la mise à jour
      const currentDoc = await getDoc(userRef);
      if (!currentDoc.exists()) {
        console.error(`❌ [UserService] Document utilisateur non trouvé: ${userId}`);
        throw new Error(`Document utilisateur non trouvé: ${userId}`);
      }

      const currentData = currentDoc.data() || {};

      // Nettoyage récursif des données pour supprimer TOUS les champs undefined
      // Firestore rejette strictement toute valeur undefined
      const sanitizeObject = (obj: any): any => {
        if (obj === null || typeof obj !== 'object') {
          return obj;
        }
        if (obj instanceof Date) {
          return obj;
        }
        if (Array.isArray(obj)) {
          return obj.map(sanitizeObject).filter(item => item !== undefined);
        }
        const cleaned: Record<string, any> = {};
        for (const [key, value] of Object.entries(obj)) {
          if (value !== undefined) {
            cleaned[key] = sanitizeObject(value);
          }
        }
        return cleaned;
      };

      const cleanData = sanitizeObject(data);

      // Protection renforcée contre l'écrasement d'abonnements payants et d'emails valides
      const updatePayload: Record<string, any> = {
        ...cleanData,
        updatedAt: new Date()
      };

      // Protection pour l'email : ne pas écraser un email valide avec une chaîne vide
      const currentEmail = currentData['email'];
      const newEmail = cleanData.email;

      // Vérifier s'il y a un email Google récemment ajouté pour cet utilisateur
      const recentGoogleEmail = localStorage.getItem(`google_email_${userId}`);
      const recentGoogleTimestamp = localStorage.getItem(`google_email_timestamp_${userId}`);
      const timeSinceGoogleUpdate = recentGoogleTimestamp ? Date.now() - parseInt(recentGoogleTimestamp) : Infinity;

      // Si un email Google a été ajouté dans les 10 dernières secondes, l'utiliser
      if (recentGoogleEmail && timeSinceGoogleUpdate < 10000) {
        if (!newEmail || newEmail === '' || newEmail === 'undefined') {
          updatePayload.email = recentGoogleEmail;
          localStorage.removeItem(`google_email_${userId}`);
          localStorage.removeItem(`google_email_timestamp_${userId}`);
        }
      } else if ((newEmail === '' || newEmail === undefined) && currentEmail && currentEmail.trim() !== '' && currentEmail.includes('@')) {
        delete updatePayload.email;
      }

      // Gestion sécurisée de l'abonnement
      const currentSubscription = currentData['subscription'] || 'free';
      const newSubscription = data.subscription;

      if (newSubscription === undefined) {
        // Pas de mise à jour demandée, on conserve l'existant
        updatePayload.subscription = currentSubscription;
      } else if (newSubscription === 'free' && currentSubscription !== 'free') {
        // Ne pas downgrader accidentellement un compte payant vers free
        updatePayload.subscription = currentSubscription;
      } else {
        updatePayload.subscription = newSubscription || 'free';
      }

      const sanitizedPayload = sanitizeObject(updatePayload);
      await updateDoc(userRef, sanitizedPayload);

      // Vérification de la mise à jour
      const updatedDoc = await getDoc(userRef);
    } catch (error) {
      console.error(`❌ [UserService] Erreur lors de la mise à jour du profil pour ${userId}:`, error);
      throw error;
    }
  },

  async completeOnboarding(userId: string, profileData: Omit<UserProfile, 'id' | 'createdAt' | 'updatedAt' | 'onboardingCompleted'>) {
    return this.updateUserProfile(userId, {
      ...profileData,
      onboardingCompleted: true
    });
  },

  async getCurrentUser() {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        // // // // // // // // // // // // // // // // console.log('Aucun utilisateur connecté');
        return null;
      }

      // Récupérer les données du profil utilisateur
      const userProfile = await this.getUserProfile(currentUser.uid);

      // Si aucun profil n'existe, retourner des informations de base de l'authentification
      if (!userProfile) {
        return {
          uid: currentUser.uid,
          email: currentUser.email || '',
          displayName: currentUser.displayName || ''
        };
      }

      // Si un profil existe, fusionner avec les données d'authentification pour être sûr d'avoir l'UID
      return {
        uid: currentUser.uid,
        ...userProfile
      };
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'utilisateur courant:', error);
      return null;
    }
  }
}; 